'use client'

import cn from 'classnames'
import jsCookie from 'js-cookie'
import {usePathname} from 'next/navigation'
import {FC, useEffect, useState} from 'react'
import {createPortal} from 'react-dom'

import {Button} from '@/components/common/content/Button'
import {ButtonSize, ButtonVariant} from '@/components/common/content/ButtonTypes'
import {Text, TextFontWeight, TextVariant} from '@/components/common/content/Text'
import {CloseIcon} from '@/components/common/icons/CloseIcon'
import {JurisdictionFlag} from '@/components/common/jurisdiction/JurisdictionFlag'
import {useUserData} from '@/components/context/UserData'
import {useTranslation} from '@/i18n'
import {IconSize} from '@/theme/icons'
import {HEADER_HEIGHT_DESKTOP, HEADER_MAX_WIDTH, HEADER_PADDING_DESKTOP} from '@/theme/sizing'
import {theme} from '@/theme/theme'
import {getCurrentPathVersionIn} from '@/utils/browser/location'
import {Locale, localeToJurisdiction} from '@/utils/constants'
import {localeCookieName} from '@/utils/constants/http'
import {useSwitchLanguage} from '@/utils/hooks/useSwitchLanguage'
import {getLanguage, isLocale, useLanguage} from '@/utils/i18n/language'
import {saveLanguageChoice} from '@/utils/preferences/choices'
import {TABLET_SCREENSIZE} from '@/utils/styles/breakpoints'
import {analytics} from '@/utils/tracking/analytics'
import {takeSignInLanguageSwitch} from '@/utils/user/client'

/*
 * Offers the visitor's language when this page has a version in it (`getCurrentPathVersionIn`),
 * instead of redirecting: a cached page is the same for everyone, so the browser decides. The
 * language is the visitor's explicit preference (profile, then the language cookie), else the
 * browser's. The offer is in the offered language itself (`AppLayout` provides every language's
 * copy). Accepting counts as picking that language in the settings; dismissing stops offering it
 * on this device.
 *
 * Right after signing in it switches instead of offering, and only to this page's own version, so
 * the visitor is never taken off the page (`markSignInLanguageSwitch`).
 *
 * See "The suggestion banner" in docs/visitor-preferences.md.
 */

type Suggestion = {
  locale: Locale
  source: 'profile' | 'cookie' | 'browser'
}

// On the element the suggestion shows under (the header's regional settings button), if any
export const LANGUAGE_SUGGESTION_ANCHOR_ID = 'language-suggestion-anchor'

const LANGUAGE_SUGGESTION_WIDTH = '350px'
const ARROW_SIZE = '12px'
// The regional settings button's padding (medium size) and its flag's default size, which the
// popup matches to put its flag right below the button's
const BUTTON_PADDING = '10px'
const FLAG_SIZE = '20px'

const dismissedKey = (locale: Locale) => `languageSuggestionDismissed:${locale}`

const isDismissed = (locale: Locale) => {
  try {
    return localStorage.getItem(dismissedKey(locale)) !== null
  } catch {
    return false
  }
}

const getCandidate = (profileLanguage: string | undefined): Suggestion | null => {
  if (profileLanguage) {
    return {locale: getLanguage(profileLanguage), source: 'profile'}
  }
  const cookieLanguage = jsCookie.get(localeCookieName)
  if (isLocale(cookieLanguage)) {
    return {locale: cookieLanguage, source: 'cookie'}
  }
  // `languages` is missing in some embedded browsers
  const browserLanguage = (navigator.languages ?? [navigator.language])
    .map((tag) => tag.split('-')[0].toLowerCase())
    .find(isLocale)
  return browserLanguage ? {locale: browserLanguage, source: 'browser'} : null
}

export const LanguageSuggestion: FC = () => {
  const language = useLanguage()
  const {t} = useTranslation()
  const pathname = usePathname()
  const {user, isAuthPending} = useUserData()
  const switchLanguage = useSwitchLanguage()
  const [suggestion, setSuggestion] = useState<Suggestion | null>(null)
  const [anchor, setAnchor] = useState<HTMLElement | null>(null)

  const userId = user?.id
  const profileLanguage = user?.profile?.language

  const switchTo = async (locale: Locale) => {
    saveLanguageChoice(locale)
    await switchLanguage(locale)
  }

  useEffect(() => {
    if (isAuthPending) {
      return
    }
    if (userId && takeSignInLanguageSwitch()) {
      const locale = profileLanguage && getLanguage(profileLanguage)
      if (locale && locale !== language && getCurrentPathVersionIn(locale)) {
        void switchTo(locale)
      }
      return
    }
    const candidate = getCandidate(profileLanguage)
    const isOffered =
      candidate !== null &&
      candidate.locale !== language &&
      getCurrentPathVersionIn(candidate.locale) !== null &&
      !isDismissed(candidate.locale)

    setSuggestion(isOffered ? candidate : null)
    if (isOffered) {
      analytics.track('Language suggestion shown', {
        from: language,
        to: candidate.locale,
        source: candidate.source,
      })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAuthPending, userId, profileLanguage, language, pathname])

  useEffect(() => {
    setAnchor(document.getElementById(LANGUAGE_SUGGESTION_ANCHOR_ID))
  }, [pathname])

  if (!suggestion) {
    return null
  }

  const accept = async () => {
    setSuggestion(null)
    analytics.track('Language suggestion accepted', {from: language, to: suggestion.locale})
    await switchTo(suggestion.locale)
  }

  const dismiss = () => {
    setSuggestion(null)
    analytics.track('Language suggestion dismissed', {from: language, to: suggestion.locale})
    try {
      localStorage.setItem(dismissedKey(suggestion.locale), '1')
    } catch {
      // storage unavailable: offered again on the next page
    }
  }

  // Read here, not in the header the card is portalled into: a page's own NextIntlClientProvider
  // there carries only the page's messages
  const tKey = `language-suggestion.${suggestion.locale}`

  const card = (
    // data-nosnippet: kept out of search result snippets
    <div className={cn('suggestion', anchor && 'anchored')} lang={suggestion.locale} data-nosnippet>
      <JurisdictionFlag jurisdiction={localeToJurisdiction[suggestion.locale]} />
      <div className="text">
        <Text variant={TextVariant.text14}>{t(`${tKey}.text`)}</Text>
      </div>
      <button type="button" className="dismiss" aria-label="Dismiss" onClick={dismiss}>
        <CloseIcon size={IconSize.small} />
      </button>
      <div className="switch">
        <Button
          type="button"
          variant={ButtonVariant.tertiary}
          size={ButtonSize.small}
          textVariant={TextVariant.subtitle11}
          onClick={accept}
          fullWidth
        >
          {t(`${tKey}.button`)}
        </Button>
      </div>

      <style jsx>{`
        .suggestion {
          display: flex;
          flex-wrap: wrap;
          align-items: flex-start;
          gap: ${theme.spacing[2.5]} ${theme.spacing[2]};
          padding: ${theme.spacing[2.5]};
          border: 1px solid ${theme.palette.gray[200]};
          border-radius: 6px;
          background-color: ${theme.palette.background.default};
          box-shadow: 0px 12px 16px -4px rgba(16, 24, 40, 0.1),
            0px 4px 6px -2px rgba(16, 24, 40, 0.05);
        }
        .anchored {
          position: absolute;
          top: calc(100% + ${theme.spacing[2]});
          left: 0;
          width: ${LANGUAGE_SUGGESTION_WIDTH};
          padding-left: ${BUTTON_PADDING};
          padding-right: ${BUTTON_PADDING};
        }
        .anchored::before {
          content: '';
          position: absolute;
          top: calc(-${ARROW_SIZE} / 2 - 1px);
          left: calc(${BUTTON_PADDING} + ${FLAG_SIZE} / 2 - ${ARROW_SIZE} / 2);
          width: ${ARROW_SIZE};
          height: ${ARROW_SIZE};
          border-top: 1px solid ${theme.palette.gray[200]};
          border-left: 1px solid ${theme.palette.gray[200]};
          border-top-left-radius: 2px;
          background-color: ${theme.palette.background.default};
          transform: rotate(45deg);
        }
        .text {
          flex: 1;
        }
        .switch {
          flex-basis: 100%;
        }
        .dismiss {
          display: flex;
          flex-shrink: 0;
        }
      `}</style>
    </div>
  )

  return (
    <>
      {anchor ? createPortal(card, anchor) : <div className="standalone">{card}</div>}
      <div className="pill" lang={suggestion.locale} data-nosnippet>
        <button type="button" className="pill-switch" onClick={accept}>
          <JurisdictionFlag jurisdiction={localeToJurisdiction[suggestion.locale]} size={18} />
          <Text variant={TextVariant.text14} element="span" fontWeight={TextFontWeight.medium}>
            {t(`${tKey}.button`)}
          </Text>
        </button>
        <button type="button" className="pill-dismiss" aria-label="Dismiss" onClick={dismiss}>
          <CloseIcon color={theme.palette.gray[600]} />
        </button>
      </div>

      <style jsx>{`
        /* desktop pages without regional settings: under the header's right edge */
        .standalone {
          position: fixed;
          top: calc(${HEADER_HEIGHT_DESKTOP} + ${theme.spacing[2]});
          right: max(${HEADER_PADDING_DESKTOP}, calc((100% - ${HEADER_MAX_WIDTH}) / 2));
          z-index: 3;
          width: ${LANGUAGE_SUGGESTION_WIDTH};
        }
        .pill {
          position: fixed;
          top: calc(${theme.spacing[2]} + env(safe-area-inset-top));
          left: 50%;
          transform: translateX(-50%);
          z-index: 3;
          display: none;
          align-items: center;
          border: 1px solid ${theme.palette.gray[100]};
          border-radius: 20px;
          background-color: ${theme.palette.background.default};
          box-shadow: 0px 4px 6px -2px rgba(16, 24, 40, 0.1);
        }
        .pill-switch {
          display: flex;
          align-items: center;
          gap: ${theme.spacing[2]};
          min-width: 0;
          padding: ${theme.spacing[1.5]} ${theme.spacing[1]} ${theme.spacing[1.5]}
            ${theme.spacing[2]};
          white-space: nowrap;
        }
        .pill-dismiss {
          display: flex;
          flex-shrink: 0;
          padding: ${theme.spacing[1.5]} ${theme.spacing[2]} ${theme.spacing[1.5]}
            ${theme.spacing[1]};
        }
        @media ${TABLET_SCREENSIZE} {
          .standalone {
            display: none;
          }
          .pill {
            display: flex;
          }
        }
      `}</style>
    </>
  )
}
